import { colors, misc } from "common/theme";
import { wait } from "mono/lib/common";
import { useCSSClassTransition } from "mono/lib/hooks/useAnimation";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import BorderedIcon from "./BorderedIcon";

export default function CookieBanner() {
  const acceptIcon = useRef();
  const rejectIcon = useRef();
  const wrapper = useRef();
  const { animateIn, animateOut } = useCSSClassTransition({ element: wrapper });

  const [visible, setVisible] = useState(false);

  // Determine initial visibility based on stored consent
  useEffect(() => {
    try {
      const stored =
        typeof window !== "undefined" && localStorage.getItem("cookieConsent");
      if (stored === "accepted" || stored === "rejected") {
        setVisible(false);
      } else {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  // Animate in when the banner becomes visible
  useEffect(() => {
    if (visible) animateIn();
  }, [visible, animateIn]);

  useEffect(() => {
    if (!visible) return;
    // First attempt immediately
    acceptIcon.current?.animateIn?.({ delay: 0 });
    rejectIcon.current?.animateIn?.({ delay: 0 });

    // In case the icons aren't mounted yet (due to client-only gating),
    // try again on the next tick once refs are set
    let cancelled = false;
    (async () => {
      await wait(50);
      if (cancelled) return;
      acceptIcon.current?.animateIn?.({ delay: 0 });
      rejectIcon.current?.animateIn?.({ delay: 0 });
    })();

    return () => {
      cancelled = true;
    };
  }, [visible]);

  const handleCookieAccept = useCallback(() => {
    try {
      localStorage.setItem("cookieConsent", "accepted");
      localStorage.setItem("cookieConsentAt", String(Date.now()));
    } catch (e) {}
    animateOut();
    // Wait for CSS transition to finish before unmounting
    wait(1000).then(() => setVisible(false));
  }, [animateOut]);

  const handleCookieReject = useCallback(() => {
    try {
      localStorage.setItem("cookieConsent", "rejected");
      localStorage.setItem("cookieConsentAt", String(Date.now()));
    } catch (e) {}
    animateOut();
    wait(1000).then(() => setVisible(false));
  }, [animateOut]);

  if (!visible) return null;

  return (
    <Wrapper
      ref={wrapper}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <h5>Deze website maakt gebruik van cookies.</h5>
      <p>
        We gebruiken cookies om uw ervaring te verbeteren, gepersonaliseerde
        advertenties of inhoud weer te geven en ons verkeer te analyseren.&nbsp;
        <span className="more-info">
          <Link href="/files/privacy-beleid.pdf">Meer info</Link>
        </span>
      </p>
      <div className="cookie-buttons">
        <BorderedIcon
          ref={acceptIcon}
          animateOnScroll={false}
          onClick={handleCookieAccept}
          role="button"
          aria-label="Alles accepteren"
          disableSplitText
          size="80rem"
          textColor={colors.outerSpace}
          text="Alles accepteren"
        ></BorderedIcon>
        <BorderedIcon
          ref={rejectIcon}
          animateOnScroll={false}
          onClick={handleCookieReject}
          role="button"
          aria-label="Alles weigeren"
          disableSplitText
          size="80rem"
          textColor={colors.outerSpace}
          text="Alles weigeren"
        ></BorderedIcon>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  z-index: 10000;
  position: fixed;
  display: inline-block;
  max-width: 800rem;

  background-color: ${colors.aquaMarine};
  color: ${colors.outerSpace};

  position: fixed;
  bottom: ${misc.basePadding};
  left: ${misc.basePadding};
  padding: 50rem;
  border-radius: 50rem;

  opacity: 0;
  transform: translateY(30rem);
  transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);

  color: ${colors.outerSpace} !important;
  * {
    color: inherit !important;
  }

  &.active {
    opacity: 1;
    transform: translateY(0);
  }

  h5 {
    font-size: 50rem;
    font-weight: 500;
  }

  p {
    margin-bottom: 30rem;
    margin-top: 30rem;
    font-size: 40rem;
  }

  h4 {
    font-size: 40rem;
    &::before {
      background: ${colors.outerSpace} !important;
    }
  }

  .more-info {
    text-decoration: underline;
    font-weight: 500;
    cursor: pointer;
    a {
      color: inherit;
    }
  }

  .cookie-buttons {
    display: flex;
    flex-direction: column;
    gap: 30rem;
    align-items: flex-start;
  }

  /* Force icon colors (border, fills, background) to stay consistent */
  .icon-container {
    .icon-wrapper {
      border-color: ${colors.outerSpace} !important;
    }

    .icon-background {
      background-color: ${colors.outerSpace} !important;
    }

    path {
      fill: ${colors.outerSpace} !important;
      stroke: ${colors.outerSpace} !important;
    }

    .icon-duplicate {
      path {
        fill: ${colors.aquaMarine} !important;
        stroke: ${colors.aquaMarine} !important;
      }
    }
  }

  .close-button {
    position: absolute;
    top: 20rem;
    right: 20rem;
    cursor: pointer;
    font-weight: 600;
    font-size: 20rem;
  }
`;
